export const MAX_DATE_INPUT = '9999-12-31';

export function isValidDateInput(value) {
  return (
    /^\d{4}-\d{2}-\d{2}$/.test(value) &&
    value >= '0001-01-01' &&
    !Number.isNaN(Date.parse(value)) &&
    toISODate(value) === value
  );
}

/** Día elegido, almacenado como fecha calendario UTC: el plazo de una
 * diligencia y la fecha ofrecida (la API guarda ambas a las 23:59:59.999Z).
 * No usar para instantes reales como pagos o actuaciones.
 */
export function formatDeadlineDate(value, format = 'full') {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const day = String(date.getUTCDate()).padStart(2, '0');
  const year = date.getUTCFullYear();
  if (format === 'numeric' || format === 'dmy') {
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    return [day, month, year].join(format === 'dmy' ? '-' : '/');
  }
  const month = date
    .toLocaleDateString('es-CL', {
      month: 'short',
      timeZone: 'UTC',
    })
    .replace(/\.$/, '');
  return format === 'short' ? `${day} ${month}` : `${day} ${month} ${year}`;
}

export function formatChileanDate(dateStr) {
  if (!dateStr) return '';
  // Soporta strings "YYYY-MM-DD" parseadas como fecha local
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  const date = isoMatch
    ? new Date(
        Number(isoMatch[1]),
        Number(isoMatch[2]) - 1,
        Number(isoMatch[3]),
      )
    : new Date(dateStr);

  if (isNaN(date)) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

// Table date format dd-mm-yyyy (hyphens, distinct from formatChileanDate's
// dd/mm/yyyy above — both are real call sites, not duplicates).
export function formatDateDMY(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}-${p(d.getMonth() + 1)}-${d.getFullYear()}`;
}

/** Milliseconds in one day. */
export const MS_PER_DAY = 86400000;

/**
 * The "YYYY-MM-DD" date part of a value's ISO/UTC timestamp.
 * @param {string|number|Date} value anything `new Date()` accepts
 * @returns {string}
 */
export function toISODate(value) {
  return new Date(value).toISOString().slice(0, 10);
}

/**
 * Formats a date as a short Spanish label, e.g. "26 jun".
 * @param {string|number|Date} value
 * @returns {string|null} The label, or null when the input is empty/invalid.
 */
export function formatChileanDateShort(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short' });
}

/**
 * Formats a date as a full Spanish label, e.g. "26 jun 2026".
 * @param {string|number|Date} value
 * @returns {string|null} The label, or null when the input is empty/invalid.
 */
export function formatChileanDateFull(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('es-CL', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * The value an `<input type="datetime-local">` expects for a given instant:
 * local wall-clock time, no zone. Defaults to now.
 * @param {Date} [at]
 * @returns {string} "YYYY-MM-DDTHH:mm"
 */
export function toLocalDateTimeInput(at = new Date()) {
  const p = (n) => String(n).padStart(2, '0');
  return (
    `${at.getFullYear()}-${p(at.getMonth() + 1)}-${p(at.getDate())}` +
    `T${p(at.getHours())}:${p(at.getMinutes())}`
  );
}

/**
 * An `<input type="datetime-local">` value as ISO 8601 **with its offset**.
 *
 * The input gives local wall-clock time with no zone, and an API that requires
 * the offset refuses it. `toISOString()` is not the fix either: it serializes
 * UTC, so an afternoon transfer in Santiago comes out on the following day and
 * the record states a date nobody chose.
 * @param {string} localValue the input's own value
 * @returns {string|undefined} undefined when the value is empty or unparseable
 */
export function localDateTimeToISO(localValue) {
  if (!localValue) return undefined;
  const d = new Date(localValue);
  if (Number.isNaN(d.getTime())) return undefined;
  const p = (n) => String(n).padStart(2, '0');
  const minutes = -d.getTimezoneOffset();
  const sign = minutes < 0 ? '-' : '+';
  const abs = Math.abs(minutes);
  return (
    `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}` +
    `T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}.000` +
    `${sign}${p(Math.floor(abs / 60))}:${p(abs % 60)}`
  );
}

/**
 * An instant as the backoffice needs to read it: date, time and seconds, in
 * Chile's timezone.
 *
 * Three decisions, each answering something the plain date could not.
 *
 * The time is there because the operator's question is *when*, and the answer
 * is used to order two steps of one day and to line a row up against the
 * provider's own panel, which shows times. A date alone answers neither.
 *
 * The seconds are there because the pairs that need ordering arrive within the
 * same minute: two webhook deliveries one second apart are indistinguishable
 * without them.
 *
 * The zone is fixed to Chile rather than taken from the browser. The instants
 * arrive in UTC and the reader is operating a Chilean business, so rendering
 * the machine's zone would show one operator a different hour than another for
 * the same event, and would move the day for anything late in the evening.
 * @param {string|number|Date} value
 * @returns {string} "DD/MM/AAAA HH:MM:SS", or '' when there is nothing to show
 */
export function formatChileanDateTime(value) {
  // Nullish and the empty string are the absence of an instant. `0` is the Unix
  // epoch, which the signature accepts as a number, and a truthiness guard
  // reads it as absence.
  if (value === null || value === undefined || value === '') return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const parts = new Intl.DateTimeFormat('es-CL', {
    day: '2-digit',
    hour: '2-digit',
    hour12: false,
    minute: '2-digit',
    month: '2-digit',
    second: '2-digit',
    timeZone: 'America/Santiago',
    year: 'numeric',
  }).formatToParts(date);
  const at = (type) => parts.find((x) => x.type === type)?.value ?? '';
  // `hourCycle` still answers "24" for midnight in some engines, so the hour is
  // normalised rather than trusted.
  const hour = at('hour') === '24' ? '00' : at('hour');
  return `${at('day')}/${at('month')}/${at('year')} ${hour}:${at('minute')}:${at('second')}`;
}

export const CHILE_TZ = 'America/Santiago';

export function chileYmd(date) {
  return new Intl.DateTimeFormat('en-CA', {
    day: '2-digit',
    month: '2-digit',
    timeZone: CHILE_TZ,
    year: 'numeric',
  }).format(date);
}

export function addCalendarDay(ymd, days) {
  const [year, month, day] = ymd.split('-').map(Number);
  const utc = new Date(Date.UTC(year, month - 1, day + days));
  return utc.toISOString().slice(0, 10);
}

/** null permite que cada superficie conserve su respaldo de staging. */
export function isDeadlineExpired(deadline, expiresAt, now = Date.now()) {
  const due = deadline ? new Date(deadline).getTime() : NaN;
  if (Number.isNaN(due)) return null;
  const nowMs = new Date(now).getTime();
  const expiry = expiresAt ? new Date(expiresAt).getTime() : NaN;
  if (!Number.isNaN(expiry)) return nowMs >= expiry;
  return null;
}

/** Día UTC elegido menos el día actual en Chile, independiente del proceso. */
export function deadlineCalendarDays(deadline, now = Date.now(), expiresAt) {
  if (isDeadlineExpired(deadline, expiresAt, now) === null) return null;
  const due = deadline ? new Date(deadline) : new Date(NaN);
  const today = new Date(now);
  if (Number.isNaN(due.getTime()) || Number.isNaN(today.getTime())) return null;
  return Math.round(
    (Date.parse(due.toISOString().slice(0, 10)) - Date.parse(chileYmd(today))) /
      MS_PER_DAY,
  );
}

/**
 * Compact Chile wall-clock, e.g. "14 sept, 15:41".
 * @param {string|number|Date} value
 * @returns {string}
 */
export function formatChileanDateTimeCompact(value) {
  if (value === null || value === undefined || value === '') return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const parts = new Intl.DateTimeFormat('es-CL', {
    day: 'numeric',
    hour: '2-digit',
    hour12: false,
    minute: '2-digit',
    month: 'short',
    timeZone: CHILE_TZ,
  }).formatToParts(date);
  const at = (type) => parts.find((x) => x.type === type)?.value ?? '';
  const hour = at('hour') === '24' ? '00' : at('hour');
  return `${at('day')} ${at('month')}, ${hour}:${at('minute')}`;
}

/**
 * Scan badge for a deadline: "6h", "Mañana", "3d", "Vencida".
 * Hours win overnight when the remaining window is under 12h; otherwise a
 * Chile calendar "tomorrow" reads as Mañana.
 * @param {string|number|Date} value
 * @param {number|Date} [now]
 * @param {string} [expiresAt] límite exclusivo aditivo de la API
 * @returns {string}
 */
export function formatDeadlineUrgencyBadge(value, now = Date.now(), expiresAt) {
  if (value === null || value === undefined || value === '') return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const nowMs = now instanceof Date ? now.getTime() : now;
  const expiry = expiresAt ? new Date(expiresAt).getTime() : NaN;
  const hasExpiry = !Number.isNaN(expiry);
  const ms = (hasExpiry ? expiry : date.getTime()) - nowMs;
  if (isDeadlineExpired(value, expiresAt, nowMs) ?? ms < 0) return 'Vencida';
  const hours = ms / 36e5;
  const nowYmd = chileYmd(new Date(nowMs));
  const dueYmd = hasExpiry ? date.toISOString().slice(0, 10) : chileYmd(date);
  if (dueYmd === nowYmd || hours < 12) {
    if (hours < 1) return `${Math.max(1, Math.round(ms / 60000))} min`;
    return `${Math.round(hours)}h`;
  }
  if (dueYmd === addCalendarDay(nowYmd, 1)) return 'Mañana';
  return `${Math.max(1, deadlineCalendarDays(value, nowMs, expiresAt) ?? Math.round(ms / MS_PER_DAY))}d`;
}
